import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { mapFromCollectionNameToSeasonName } from "@constants/mappers";
import { useQuery } from "@tanstack/react-query";

import { type PurchaseOption } from "@lib/api/checkout.types";
import { fetchUserData } from "@lib/api/customerApi";
import { fetchMemberExperiments } from "@lib/api/experimentsApi";
import {
  fetchUserPreferences,
  type UserPreferences,
} from "@lib/api/userPreferencesApi";
import {
  type HomeExperience,
  isMemberHomeArm,
  MEMBER_HOME_ARM_TRAIT,
  type MemberHomeArm,
  resolveHomeExperience,
} from "@lib/experiments/memberHome";
import { getProductPricingQueryOptions } from "@lib/helpers/getProductPricingQueryOptions";
import { memberPurchaseOption } from "@lib/helpers/memberPurchaseOption";
import useAnalytics from "@lib/hooks/useAnalytics";
import { getUserPreferencesQueryKey } from "@lib/hooks/useGetUserPreferences";
import {
  SEASON_2_TECHNIQUE_PAGES,
  SEASON_3_TECHNIQUE_PAGES,
} from "@pages/collections/types/Page.types";
import type { TechniqueSeason } from "@root/constants";
import { setSentryUser } from "@utils/sentry";

import { useAuthenticatedUser } from "./AuthenticationContext";
import { useLocale } from "./LocaleContext";

interface NmhContextType {
  userPreferences: UserPreferences | undefined;
  isLoadingUserPreferences: boolean;
  /**
   * True until the getMe request that carries the entitlements has an answer
   * (success or error). Entitlements used to be a union with a synchronous
   * ID-token claim, so they were known on the first render; since OMG-2081
   * removed that claim, getMe is the only source and every access decision
   * (`hasAccessToTechnique`, `userHasFullAccess`, `hasEntitlement`) reads as
   * "no access" until it resolves. Guards must wait on this flag or a cold
   * load of a technique URL redirects away (OMG-2483).
   *
   * MemberAccessBoundary also handles isEntitlementsError before any access
   * decision is mounted. The query runs even offline with a bounded deadline
   * (OMG-2575); failure is unknown access, not a confirmed empty grant list.
   */
  isEntitlementsPending: boolean;
  isEntitlementsError: boolean;
  isEntitlementsFetching: boolean;
  retryEntitlements: () => void;
  userHasFullAccess: boolean;
  /** The SKU /upsell sells this member: the upgrade for a partial owner, the
   *  full bundle for one who owns nothing (OMG-2717). */
  memberPurchaseOption: PurchaseOption;
  hasAccessToTechnique: (techniqueId: string) => boolean;
  hasEntitlement: (season: TechniqueSeason) => boolean;
  /**
   * Account creation date (ISO 8601) for age-based UI (e.g. the 30-day
   * ArticleSelector toggle). Comes from getMe's createdAt; the old
   * firstNmhAccessDate token-claim fallback was removed with the
   * token-enrichment Lambda (OMG-2081).
   */
  accountCreatedAt: string | undefined;
  /**
   * Collections Home A/B test (OMG-2581): which home this member gets.
   * `nmh` = today's member home, `collections` = the collections page (also
   * every non-English member, test or no test), `pending` = English member
   * whose arm request is still in flight. Read this, not the locale, for
   * every home-shaped decision (/home routing, the collections page's way
   * back home, whether Articles / Date Nights exist for this member).
   */
  homeExperience: HomeExperience;
  /** The raw arm from omg-api, or null when not in the test / unknown. Only
   *  `collections` changes behaviour; prefer `homeExperience` for routing. */
  memberHomeArm: MemberHomeArm | null;
}

const NmhContext = createContext<NmhContextType | undefined>(undefined);

export const NmhProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const authenticatedUser = useAuthenticatedUser();
  const { analytics } = useAnalytics();
  const { locale, settledLocale } = useLocale();

  // Scope cached access to this member. Even offline, attempt the bounded
  // request so the route boundary can offer recovery instead of a paused loader.
  const {
    data: userData,
    isPending: isEntitlementsPending,
    isError: isEntitlementsError,
    isFetching: isEntitlementsFetching,
    refetch: refetchEntitlements,
  } = useQuery({
    queryKey: ["customers", "me", authenticatedUser.sub],
    queryFn: () => fetchUserData(authenticatedUser.sub),
    retry: false,
    networkMode: "always",
  });

  // Fire user-preferences fetch as soon as the user logs in. NmhProvider mounts
  // post-RequireAuth, so `authenticatedUser.sub` is already resolved
  // synchronously — building the query key from it (instead of the async
  // `useGetUserPreferencesQueryOptions` Amplify lookup) means the query is
  // enabled on the first render, so `isLoadingUserPreferences` is true while it
  // fetches rather than briefly reading as "not loading, no data". Consumers
  // inside the auth subtree (every VideoPlayer) read from here instead of
  // calling the hook themselves, avoiding N redundant Cognito lookups.
  const { data: userPreferences, isLoading: isLoadingUserPreferences } =
    useQuery({
      queryKey: getUserPreferencesQueryKey(authenticatedUser?.sub),
      queryFn: fetchUserPreferences,
      enabled: !!authenticatedUser?.sub,
      retry: false,
    });

  // Collections Home A/B test (OMG-2581). One request per session: the arm is
  // frozen server-side, so the answer never goes stale (staleTime Infinity).
  // Keyed on the SETTLED locale (LocaleContext), never i18next's first guess:
  // the server freezes eligibility on the locale it is handed, and
  // `resolvedLanguage` only flips after changeLanguage has fetched the new
  // namespace, so a member whose `loc` cookie disagrees with the guess would
  // otherwise be evaluated (and frozen) under the wrong language. The query
  // is disabled until then — one effect tick, no network.
  // networkMode "always" (never "paused" offline) + the deadline inside
  // fetchMemberExperiments guarantee the query SETTLES; on error the arm
  // reads null and the member gets today's home (fail open).
  const experimentsEnabled = !!authenticatedUser?.sub && settledLocale !== null;
  const { data: memberExperiments, isPending: isMemberExperimentsPending } =
    useQuery({
      queryKey: [
        "experiments",
        "member",
        authenticatedUser?.sub,
        settledLocale,
      ],
      queryFn: () => fetchMemberExperiments(settledLocale as string),
      enabled: experimentsEnabled,
      retry: false,
      staleTime: Infinity,
      networkMode: "always",
    });
  // Validated, not cast: a wire value this build does not recognise is "not
  // in the test", which is today's home.
  const memberHomeArm: MemberHomeArm | null = isMemberHomeArm(
    memberExperiments?.memberHome,
  )
    ? memberExperiments.memberHome
    : null;
  const homeExperience = resolveHomeExperience({
    locale: settledLocale,
    arm: memberHomeArm,
    // A disabled query is also "pending" to react-query; only a real
    // in-flight request should hold the loader.
    pending: experimentsEnabled && !!isMemberExperimentsPending,
  });

  // Entitlements come from getMe alone. The old union with the omgyes
  // token claim was removed with the token-enrichment Lambda (OMG-2081);
  // getMe already reads both the new tables and the legacy Postgres tables.
  const userEntitlements: TechniqueSeason[] = [
    ...new Set(userData?.entitlements ?? []),
  ];

  const userHasFullAccess = Object.values(
    mapFromCollectionNameToSeasonName,
  ).every((entitlement) =>
    userEntitlements.includes(entitlement as TechniqueSeason),
  );

  const purchaseOption = memberPurchaseOption({
    entitlements: userEntitlements,
    isEntitlementsError,
  });

  useQuery(
    getProductPricingQueryOptions({
      enabled: !!userData && !isEntitlementsError && !userHasFullAccess,
      option: purchaseOption,
    }),
  );

  const hasEntitlement = useCallback(
    (season: TechniqueSeason) => userEntitlements.includes(season),
    [userEntitlements],
  );

  const hasAccessToTechnique = useCallback(
    (techniqueId: string) => {
      if (
        (SEASON_2_TECHNIQUE_PAGES as readonly string[]).includes(techniqueId)
      ) {
        return userEntitlements.includes("season-2");
      }
      if (
        (SEASON_3_TECHNIQUE_PAGES as readonly string[]).includes(techniqueId)
      ) {
        return userEntitlements.includes("season-3");
      }
      // Season 1 techniques are always accessible
      return true;
    },
    [userEntitlements],
  );

  useEffect(() => {
    if (authenticatedUser?.sub) {
      analytics.identify(authenticatedUser.sub, {
        locale,
        email: authenticatedUser.email,
        entitlements: userEntitlements,
      });

      setSentryUser({
        id: authenticatedUser.sub,
        email: authenticatedUser.email,
      });
    }
  }, [authenticatedUser]);

  // Stamp the arm as an identify trait on the new_member_home source — the
  // BigQuery readout joins first-week engagement to it via
  // new_member_home.identifies. Only for real enrolments (memberHomeAssigned):
  // a post-test winner forced for everyone must not look like an assignment.
  // Separate from the identify above because the arm arrives later and traits
  // merge server-side; scrubEvent's TRAIT_ALLOWLIST must carry the key.
  useEffect(() => {
    if (
      authenticatedUser?.sub &&
      memberExperiments?.memberHomeAssigned &&
      memberHomeArm
    ) {
      analytics.identify(authenticatedUser.sub, {
        [MEMBER_HOME_ARM_TRAIT]: memberHomeArm,
      });
    }
  }, [
    authenticatedUser?.sub,
    memberExperiments?.memberHomeAssigned,
    memberHomeArm,
  ]);

  const accountCreatedAt = userData?.createdAt ?? undefined;

  const value = {
    userPreferences,
    isLoadingUserPreferences,
    isEntitlementsPending,
    isEntitlementsError,
    isEntitlementsFetching,
    retryEntitlements: () => {
      void refetchEntitlements({ cancelRefetch: false });
    },
    userHasFullAccess,
    memberPurchaseOption: purchaseOption,
    hasAccessToTechnique,
    hasEntitlement,
    accountCreatedAt,
    homeExperience,
    memberHomeArm,
  };
  return <NmhContext.Provider value={value}>{children}</NmhContext.Provider>;
};

export const useNmh = () => {
  const context = useContext(NmhContext);
  if (context === undefined) {
    throw new Error("useNmh must be used within a NmhProvider");
  }
  return context;
};
